import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDetectionZonePromise, getPresenceZonePromise, getExclusionZonePromise } from 'src/utils/api';
import { useSearch } from 'src/hooks/use-search';
import { QUERY_KEY_MAP } from 'src/constants';
import { useActiveZoneEditorType } from 'src/components/ZoneEditor/hooks/use-active-zone-editor-type/useActiveZoneEditorType';
import { ZONE_TYPE_MAP, type ZoneTypeMap } from 'src/components/ZoneEditor/constants';
import { useIsZoneSandboxContext } from 'src/hooks/use-is-zone-sandbox-context';

const useGetDetectionZoneData = () => {
  const [searchParams] = useSearch();
  const deviceId = searchParams?.device?.id || '';
  const { isDetectionZoneEditor } = useActiveZoneEditorType();
  const isZoneSandboxContext = useIsZoneSandboxContext();

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: [QUERY_KEY_MAP.useGetDetectionZoneData, deviceId],
    queryFn: () => getDetectionZonePromise(deviceId),
    enabled: !isZoneSandboxContext && isDetectionZoneEditor && !!deviceId,
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  };
};

const useGetPresenceZoneData = () => {
  const [searchParams] = useSearch();
  const { isPresenceZoneEditor } = useActiveZoneEditorType();
  const deviceId = searchParams?.device?.id || '';
  const isZoneSandboxContext = useIsZoneSandboxContext();

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: [QUERY_KEY_MAP.useGetPresenceZoneData, deviceId],
    queryFn: () => getPresenceZonePromise(deviceId),
    enabled: !isZoneSandboxContext && isPresenceZoneEditor && !!deviceId,
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  };
};

const useGetExclusionZoneData = () => {
  const [searchParams] = useSearch();
  const { isExclusionZoneEditor } = useActiveZoneEditorType();
  const deviceId = searchParams?.device?.id || '';
  const isZoneSandboxContext = useIsZoneSandboxContext();

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: [QUERY_KEY_MAP.useGetExclusionZoneData, deviceId],
    queryFn: () => getExclusionZonePromise(deviceId),
    enabled: !isZoneSandboxContext && isExclusionZoneEditor && !!deviceId,
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  };
};

const QUERY_HOOK_MAP = {
  [ZONE_TYPE_MAP.detection]: useGetDetectionZoneData,
  [ZONE_TYPE_MAP.presence]: useGetPresenceZoneData,
  [ZONE_TYPE_MAP.exclusion]: useGetExclusionZoneData,
} as const;

export const useGetZoneData = () => {
  const [searchParams] = useSearch();
  const queryClient = useQueryClient();
  const deviceId = searchParams?.device?.id || '';
  const isZoneSandboxContext = useIsZoneSandboxContext();

  const queryHook = QUERY_HOOK_MAP[searchParams?.zone_type as ZoneTypeMap] || QUERY_HOOK_MAP[ZONE_TYPE_MAP.detection];

  // seed cache for the other query
  if (deviceId && !isZoneSandboxContext) {
    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_MAP.useGetDetectionZoneData, deviceId],
      queryFn: () => getDetectionZonePromise(deviceId),
    });

    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_MAP.useGetPresenceZoneData, deviceId],
      queryFn: () => getPresenceZonePromise(deviceId),
    });

    queryClient.prefetchQuery({
      queryKey: [QUERY_KEY_MAP.useGetExclusionZoneData, deviceId],
      queryFn: () => getExclusionZonePromise(deviceId),
    });
  }

  if (isZoneSandboxContext) {
    return {
      isLoading: false,
      isError: false,
      error: null,
      isRefetching: false,
      data: undefined,
      refetch: () => {},
    };
  }

  return queryHook();
};

export const useGetZoneCounts = () => {
  const { data: detectionData } = useGetDetectionZoneData();
  const { data: presenceData } = useGetPresenceZoneData();
  const { data: exclusionData } = useGetExclusionZoneData();

  return {
    detectionCount: detectionData?.length || 0,
    presenceCount: presenceData?.length || 0,
    exclusionCount: exclusionData?.length || 0,
  };
};
